import {
	Injectable,
	OnDestroy
}                                from '@angular/core';
import {
	IListOperationsConfig,
	ListOperations,
	ListOperationsIdentifierEnum
}                                from '@shared/list-operations/model/list-operations.model';
import { IPaging }               from '@shared/list-operations/model/paging.model';
import { ListOperationsService } from '@shared/list-operations/services/list-operations.service';
import { LoggerService }         from '@shared/services/logger.service';
import {
	Observable,
	Subject
}                                from 'rxjs';
import {
	map,
	take,
	takeUntil
}                                from 'rxjs/operators';

// Provide ListOperationsHelperService as instance in component decorator
@Injectable()
export class ListOperationsHelperService implements OnDestroy {

	private _unsubscribe: Subject<void> = new Subject();
	private _listOperationId: ListOperationsIdentifierEnum;
	private _listOperationsConfigData: IListOperationsConfig;
	private _isOnDestroy = false;

	paging$: Observable<IPaging>;
	listOperationsConfigData$: Observable<IListOperationsConfig>;

	constructor(
		private readonly listOperationsService: ListOperationsService,
		private readonly logger: LoggerService
	) {}

	ngOnDestroy(): void {
		this._isOnDestroy = true;
		this.clearListOperation();
	}

	get isOnDestroy() {
		return this._isOnDestroy;
	}

	unsubscribeListOperation(): void {
		this._unsubscribe.next();
		this._unsubscribe.complete();
	}

	clearListOperation(): void {
		this.unsubscribeListOperation();

		if ( this._listOperationId ) {
			this.listOperationsService.state.listOperationsClearAndKeepRestoreData(
				this._listOperationId,
				'ListOperationsHelperService:ngOnDestroy'
			);
		}
	}

	initListOperation(
		listOperationsConfigData: IListOperationsConfig,
		caller: string,
		listOperationsConfigDataMapCallBack?: ( listOperationsConfigData: IListOperationsConfig ) => IListOperationsConfig
	): void {
		this._listOperationId = listOperationsConfigData?.id;

		if ( !this._listOperationId ) {
			this.logger.error( `${ caller } didn't provide listOperationsConfigData with id` );
		}

		// upserts listOperations store entry (id only)
		this.listOperationsService.state.listOperationsUpsert(
			{ id: listOperationsConfigData?.id },
			caller
		);

		this.paging$ =
			this.listOperationsService.select.paging( this._listOperationId, caller )
			.pipe( takeUntil( this._unsubscribe ) );


		this.listOperationsConfigData$ =
			this.listOperationsService.select.configData( this._listOperationId, caller )
			.pipe(
				take( 1 ),
				takeUntil( this._unsubscribe ),
				map( ( storeListOperationsConfigData: IListOperationsConfig ) => {
					const defaultListOperationsConfigData = this._listOperationsConfigData = {
						...listOperationsConfigData,
						restoreListOperationsData: storeListOperationsConfigData?.restoreListOperationsData
					};

					const mappedListOperationsConfigData = listOperationsConfigDataMapCallBack
																								 ? listOperationsConfigDataMapCallBack( defaultListOperationsConfigData )
																								 : defaultListOperationsConfigData;

					this.logger.debug(
						'listOpsHS: initListOperation->listOpsConfig passed to method', listOperationsConfigData,
						'->listOpsConfig returned from store', storeListOperationsConfigData,
						'->listOpsConfig after mapping', mappedListOperationsConfigData
					);

					return this.listOperationsService.helper.sanitizeListOpsConfigToUserPermissions( mappedListOperationsConfigData );
				} )
			);


	}

	onListOperationChange( listOperations: ListOperations, caller: string ): void {
		this.listOperationsService.state.listOperationsUpsert( listOperations, caller );
	}

	setActiveListOperationsId( activeListOperationsId: ListOperationsIdentifierEnum, caller: string ): void {
		this.listOperationsService.state.activeListOperationsIdSet(
			activeListOperationsId,
			caller
		);
	}

	setListOperationsRestoreFlag( shouldRestoreData: boolean, caller: string ): void {
		this.listOperationsService.state.listOperationsSetRestoreFlag(
			this._listOperationId,
			shouldRestoreData,
			caller
		);
	}
}
